import React from 'react';
import { Plus, Search, X } from 'lucide-react';
import { DateRangeFilterDropdown } from '../DateRangeFilterDropdown';
import type { EmployeeMultiSelectOption } from '../employees/EmployeeMultiSelect';
import { isFilterMultiSelectNone } from '../filterMultiSelectUtils';
import { TRADE_STYLES, type TradeKey } from '../../utils/categoryToTrade';
import { FALLBACK_ACTIVITY_TYPES } from '../../utils/activityIcons';
import {
  buildPlantafelTypeFilterOptions,
  SCHEDULING_STATUS_FILTER_OPTIONS,
} from './plantafelTypes';
import { CalendarViewSwitcher } from './CalendarViewSwitcher';
import { PlantafelToolbarFilters } from './PlantafelToolbarFilters';
import {
  PLANTAFEL_FILTER_TRIGGER_CLASS,
  PLANTAFEL_SEARCH_INPUT_CLASS,
} from './plantafelToolbarStyles';
import type { ActivityTypeOption } from './activityListFormat';

export type ActivityListFilterProject = {
  leadId: string;
  title: string;
  customer: string;
  trade: string;
};

type ActivityListToolbarProps = {
  instancePrefix: string;
  resultCount: number;
  dateRange: { from: string | null; to: string | null };
  onDateRangeChange: (value: { from: string | null; to: string | null }) => void;
  employees: EmployeeMultiSelectOption[];
  projects: ActivityListFilterProject[];
  activityTypes: ActivityTypeOption[];
  mitarbeiter: string[];
  projekt: string[];
  typ: string[];
  status: string[];
  search: string;
  onMitarbeiterChange: (ids: string[]) => void;
  onProjektChange: (ids: string[]) => void;
  onTypChange: (ids: string[]) => void;
  onStatusChange: (ids: string[]) => void;
  onSearchChange: (value: string) => void;
  onResetFilters: () => void;
  onCreateActivity: () => void;
};

function formatDateChip(ymd: string): string {
  const d = new Date(`${ymd}T12:00:00`);
  if (Number.isNaN(d.getTime())) return ymd;
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
}

export function ActivityListToolbar({
  instancePrefix,
  resultCount,
  dateRange,
  onDateRangeChange,
  employees,
  projects,
  activityTypes,
  mitarbeiter,
  projekt,
  typ,
  status,
  search,
  onMitarbeiterChange,
  onProjektChange,
  onTypChange,
  onStatusChange,
  onSearchChange,
  onResetFilters,
  onCreateActivity,
}: ActivityListToolbarProps) {
  const typeOptions = activityTypes.length > 0 ? activityTypes : FALLBACK_ACTIVITY_TYPES;

  const filterChips: Array<{ id: string; label: string; onRemove: () => void }> = [];

  if (dateRange.from || dateRange.to) {
    const fromLabel = dateRange.from ? formatDateChip(dateRange.from) : '…';
    const toLabel = dateRange.to ? formatDateChip(dateRange.to) : '…';
    filterChips.push({
      id: 'date',
      label: `Zeitraum: ${fromLabel} – ${toLabel}`,
      onRemove: () => onDateRangeChange({ from: null, to: null }),
    });
  }

  if (mitarbeiter.length > 0 && !isFilterMultiSelectNone(mitarbeiter)) {
    const names = mitarbeiter
      .map((id) => employees.find((e) => e.id === id)?.name)
      .filter(Boolean) as string[];
    filterChips.push({
      id: 'mitarbeiter',
      label:
        names.length <= 2
          ? `Mitarbeiter: ${names.join(', ') || mitarbeiter.length}`
          : `Mitarbeiter: ${names.slice(0, 2).join(', ')} +${names.length - 2}`,
      onRemove: () => onMitarbeiterChange([]),
    });
  } else if (isFilterMultiSelectNone(mitarbeiter)) {
    filterChips.push({
      id: 'mitarbeiter-none',
      label: 'Mitarbeiter: keine',
      onRemove: () => onMitarbeiterChange([]),
    });
  }

  if (projekt.length > 0 && !isFilterMultiSelectNone(projekt)) {
    filterChips.push({
      id: 'projekt',
      label:
        projekt.length === 1
          ? `Projekt: ${projects.find((p) => p.leadId === projekt[0])?.title ?? '1'}`
          : `Projekte: ${projekt.length}`,
      onRemove: () => onProjektChange([]),
    });
  } else if (isFilterMultiSelectNone(projekt)) {
    filterChips.push({
      id: 'projekt-none',
      label: 'Projekte: keine',
      onRemove: () => onProjektChange([]),
    });
  }

  if (typ.length > 0 && !isFilterMultiSelectNone(typ)) {
    filterChips.push({
      id: 'typ',
      label: typ.length === 1 ? `Typ: 1 ausgewählt` : `Typen: ${typ.length}`,
      onRemove: () => onTypChange([]),
    });
  } else if (isFilterMultiSelectNone(typ)) {
    filterChips.push({
      id: 'typ-none',
      label: 'Typen: keine',
      onRemove: () => onTypChange([]),
    });
  }

  if (status.length > 0 && !isFilterMultiSelectNone(status)) {
    const labels = status
      .map((id) => SCHEDULING_STATUS_FILTER_OPTIONS.find((o) => o.id === id)?.label ?? id)
      .filter(Boolean);
    filterChips.push({
      id: 'status',
      label:
        labels.length <= 2
          ? `Status: ${labels.join(', ')}`
          : `Status: ${labels.slice(0, 2).join(', ')} +${labels.length - 2}`,
      onRemove: () => onStatusChange([]),
    });
  } else if (isFilterMultiSelectNone(status)) {
    filterChips.push({
      id: 'status-none',
      label: 'Status: keine',
      onRemove: () => onStatusChange([]),
    });
  }

  const hasActiveFilters = filterChips.length > 0;

  const projektOptions = projects.map((p) => {
    const T = TRADE_STYLES[(p.trade as TradeKey) in TRADE_STYLES ? (p.trade as TradeKey) : 'wartung'];
    return {
      id: p.leadId,
      label: p.title,
      subtitle: p.customer,
      leading: (
        <span
          className="inline-block h-2 w-2 shrink-0 rounded-sm"
          style={{ background: T.solid }}
          aria-hidden
        />
      ),
    };
  });

  const resetDropdownFilters = () => {
    onMitarbeiterChange([]);
    onProjektChange([]);
    onTypChange([]);
    onStatusChange([]);
  };

  return (
    <div className="shrink-0 border-b border-app-border bg-app-surface">
      <div className="flex w-full flex-wrap items-center gap-3 px-4 py-2.5">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <CalendarViewSwitcher instancePrefix={instancePrefix} active="list" />
          <p
            className="shrink-0 whitespace-nowrap text-sm tabular-nums text-app-text-subtle"
            aria-live="polite"
          >
            {resultCount === 0
              ? 'Keine Einträge'
              : resultCount === 1
                ? '1 Eintrag'
                : `${resultCount} Einträge`}
          </p>
        </div>

        <PlantafelToolbarFilters
          employees={employees}
          projektOptions={projektOptions}
          typOptions={buildPlantafelTypeFilterOptions(typeOptions)}
          mitarbeiter={mitarbeiter}
          projekt={projekt}
          typ={typ}
          status={status}
          onMitarbeiterChange={onMitarbeiterChange}
          onProjektChange={onProjektChange}
          onTypChange={onTypChange}
          onStatusChange={onStatusChange}
          onResetFilters={resetDropdownFilters}
          leading={
            <DateRangeFilterDropdown
              value={dateRange}
              onChange={onDateRangeChange}
              triggerClassName={PLANTAFEL_FILTER_TRIGGER_CLASS}
            />
          }
        >
          <div className="relative w-full min-w-[160px] max-w-[220px] sm:w-[200px]">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-app-text-subtle"
              aria-hidden
            />
            <input
              type="search"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Suchen…"
              className={`${PLANTAFEL_SEARCH_INPUT_CLASS} [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none`}
              aria-label="Aktivitäten durchsuchen"
            />
            {search ? (
              <button
                type="button"
                onClick={() => onSearchChange('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-app-text-muted"
                aria-label="Suche löschen"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>
          <button
            type="button"
            className="btn-pipedrive-green inline-flex !h-9 shrink-0 items-center gap-1.5 !rounded-md !px-3.5 text-sm font-semibold"
            onClick={onCreateActivity}
          >
            <Plus className="h-4 w-4" />
            Aktivität
          </button>
        </PlantafelToolbarFilters>
      </div>

      {hasActiveFilters || search.trim() ? (
        <div className="flex flex-wrap items-center gap-2 border-t border-app-border px-4 py-2">
          {filterChips.map((chip) => (
            <button
              key={chip.id}
              type="button"
              onClick={chip.onRemove}
              className="inline-flex h-7 items-center gap-1 rounded-full bg-app-surface-muted px-2.5 text-xs font-medium text-app-text-muted transition-colors hover:bg-app-hover"
            >
              <span className="max-w-[220px] truncate">{chip.label}</span>
              <X className="h-3 w-3 shrink-0 text-app-text-subtle" aria-hidden />
            </button>
          ))}
          {hasActiveFilters ? (
            <button
              type="button"
              onClick={onResetFilters}
              className="inline-flex h-7 items-center gap-1 rounded-full px-2 text-xs font-medium text-app-text-subtle transition-colors hover:bg-app-hover hover:text-app-text-muted"
            >
              <X className="h-3 w-3" />
              Alle zurücksetzen
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
